import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getServiceEventByAssignmentId,
  getServiceEventsByAssignmentIds
} from '@/lib/supabase/serviceEvents';
import type { ServiceEventCompletion } from '@/types/serviceEvents';

export const serviceEventKeys = {
  all: ['service-events'] as const,
  byAssignment: (id: string) => [...serviceEventKeys.all, 'assignment', id] as const,
  byAssignmentBatch: (ids: string[]) =>
    [...serviceEventKeys.all, 'batch', [...ids].sort().join(',')] as const
};

export function useServiceEventCompletion(assignmentId: string | undefined) {
  return useQuery({
    queryKey: serviceEventKeys.byAssignment(assignmentId ?? ''),
    queryFn: () => getServiceEventByAssignmentId(assignmentId as string),
    enabled: !!assignmentId,
    // Completion data is immutable once written, so 5 min staleTime is conservative.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });
}

// Batched fetch for "which of these completed recalls actually have captured details?"
// Used at the Route Tracker page level to gate the click affordance — rows without
// data stay non-clickable, preserving the pre-feature behavior for legacy completions.
// Also primes the per-assignment React Query cache so the drawer renders without a
// follow-up fetch.
export function useVisibleServiceEventsMap(assignmentIds: string[]): {
  data: Map<string, ServiceEventCompletion> | undefined;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: serviceEventKeys.byAssignmentBatch(assignmentIds),
    queryFn: () => getServiceEventsByAssignmentIds(assignmentIds),
    enabled: assignmentIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!query.data) return;
    // Prime per-assignment cache so the drawer's single-row hook hits warm cache.
    query.data.forEach((event, id) => {
      queryClient.setQueryData(serviceEventKeys.byAssignment(id), event);
    });
  }, [query.data, queryClient]);

  return { data: query.data, isLoading: query.isLoading };
}
